import { Component } from '@theme/component';

/**
 * Kræv størrelsesvalg: stopper køb, indtil kunden selv har valgt en størrelse.
 *
 * Forvalget fjernes af det inline script i blocks/locktown-require-size.liquid,
 * som også sætter data-applied="true". Uden det gør komponenten ingenting.
 *
 * Så længe der ikke er valgt størrelse, fanger komponenten klik på købsknappen og
 * knappen i sticky-baren og viser en besked i stedet. Horizons on:click og
 * on:submit lytter i capture-fasen på document (assets/component.js), så vi lytter
 * i capture-fasen på window for at komme før dem. Vælger kunden en størrelse, slipper den igen. Horizon henter
 * knapperne på ny og sætter ?variant= på URL'en, så valget huskes ved reload.
 */

const BUY_BUTTONS = 'product-form-component [ref="addToCartButton"], .sticky-add-to-cart__button';
const PROMPT_CLASS = 'locktown-require-size__fieldset--prompt';
/** Lidt længere end Horizons --animation-speed. */
const SETTLE_MS = 600;

/**
 * @typedef {Object} LocktownRequireSizeRefs
 * @property {HTMLElement} message
 * @property {HTMLElement} messageText
 */

/** @extends {Component<LocktownRequireSizeRefs>} */
class LocktownRequireSize extends Component {
  /** @type {HTMLElement | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.applied !== 'true') return;

    this.#section = this.closest('.shopify-section');
    window.addEventListener('click', this.#onClick, true);
    window.addEventListener('submit', this.#onSubmit, true);
    this.#section?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#release();
  }

  /** Radioerne for den valgmulighed, der skal vælges. */
  get #inputs() {
    const picker = this.#section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const name = (this.dataset.optionName || '').trim().toLowerCase();
    return Array.from(picker?.querySelectorAll('fieldset input[type="radio"]') ?? []).filter(
      (input) => input instanceof HTMLInputElement && (input.dataset.optionName || '').trim().toLowerCase() === name
    );
  }

  /**
   * Hører eventet til købsknapperne i vores egen sektion?
   * @param {Event} event
   * @param {string} selector
   */
  #isOurs(event, selector) {
    const { target } = event;
    return target instanceof Element && !!this.#section?.contains(target) && !!target.closest(selector);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#isOurs(event, BUY_BUTTONS)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#prompt();
  };

  /**
   * Fx Enter i antalsfeltet.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (!this.#isOurs(event, 'product-form-component')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#prompt();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !this.#inputs.includes(event.target)) return;
    if (!event.target.checked) return;
    this.#restoreLabels();
    this.#release();

    // Hold de andre knappers pill skjult, mens de glider ud af syne. Se CSS'en i blocken.
    this.dataset.settling = '';
    setTimeout(() => delete this.dataset.settling, SETTLE_MS);
  };

  #prompt() {
    const inputs = this.#inputs;
    const fieldset = inputs[0]?.closest('fieldset');

    // Teksten sættes først nu, så skærmlæsere læser den op ved hvert klik.
    this.refs.message.hidden = false;
    this.refs.messageText.textContent = '';
    requestAnimationFrame(() => {
      this.refs.messageText.textContent = this.dataset.message || '';
    });

    if (fieldset) {
      // Genstart animationen, også ved flere klik i træk.
      fieldset.classList.remove(PROMPT_CLASS);
      void fieldset.offsetWidth;
      fieldset.classList.add(PROMPT_CLASS);

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      fieldset.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    const first = inputs.find((input) => input.getAttribute('aria-disabled') !== 'true') ?? inputs[0];
    first?.focus({ preventScroll: true });
  }

  /**
   * Sætter knapteksterne tilbage med det samme. Horizon henter dem også fra
   * serveren efter variant-skiftet, men det tager et øjeblik.
   */
  #restoreLabels() {
    const section = this.#section;
    if (!section) return;

    const button = section.querySelector('product-form-component [ref="addToCartButton"]');
    if (button instanceof HTMLElement && button.dataset.locktownLabel !== undefined) {
      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim() !== ''
      );
      if (textNode) textNode.textContent = button.dataset.locktownLabel;
      delete button.dataset.locktownLabel;
    }

    const stickyText = section.querySelector('.sticky-add-to-cart__button .add-to-cart-text__content > span');
    if (stickyText instanceof HTMLElement && stickyText.dataset.locktownLabel !== undefined) {
      stickyText.textContent = stickyText.dataset.locktownLabel;
      delete stickyText.dataset.locktownLabel;
    }
  }

  #release() {
    this.#inputs[0]?.closest('fieldset')?.classList.remove(PROMPT_CLASS);
    if (this.refs.message) this.refs.message.hidden = true;
    this.dataset.applied = 'false';

    window.removeEventListener('click', this.#onClick, true);
    window.removeEventListener('submit', this.#onSubmit, true);
    this.#section?.removeEventListener('change', this.#onChange);
  }
}

if (!customElements.get('locktown-require-size-component')) {
  customElements.define('locktown-require-size-component', LocktownRequireSize);
}
